import { wizard } from "@msys/question-isomorphic";
import { ButtonSelect, useLocalStorageAsState } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { PermissionName } from "../../../clients/graphqlTypes.js";
import { useWizardQuestion } from "../../../clients/openapi/client.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";

export type WizardContext =
  | "item"
  | "onQuoteRefinement"
  | "onQuoteRefinement2"
  | "onQuoteRefinement3";

export function useWizardContext(document: {
  viewerPermissions: PermissionName[];
}) {
  const { t } = useTranslate(["QuoteEdit", "Decisions"]);

  const [activeContext, setActiveContext] =
    useLocalStorageAsState<WizardContext>(
      "quote-wizard-active-context",
      "onQuoteRefinement"
    );

  const wizardVisibilityOptions: Array<{
    value: WizardContext;
    label: string;
  }> = [
    { value: "item", label: t("Item", { ns: "QuoteEdit" }) },
    {
      value: "onQuoteRefinement",
      label: t("Quote refinement wizard", { ns: "Decisions" }),
    },
    {
      value: "onQuoteRefinement2",
      label: t("Quote refinement wizard2", { ns: "Decisions" }),
    },
    {
      value: "onQuoteRefinement3",
      label: t("Quote refinement wizard3", { ns: "Decisions" }),
    },
  ];

  const selectContextButton = (
    <RestrictedByDocumentPermissionWithDebug
      permission="TAKE_QUOTES_DECISION"
      document={document}
    >
      <ButtonSelect
        label={t("Wizard", { ns: "QuoteEdit" })}
        options={wizardVisibilityOptions}
        value={activeContext}
        searchLabel=""
        noOptionsLabel=""
        onChange={value => {
          if (value) {
            setActiveContext(value);
          }
        }}
        showCount={false}
      />
    </RestrictedByDocumentPermissionWithDebug>
  );

  return { selectContextButton, activeContext };
}

export function useTreeItemIdsWithWizardQuestions({
  sourceDoc,
  wizardContext,
}: {
  sourceDoc: wizard.SourceDoc;
  wizardContext: WizardContext;
}) {
  const { data, refetch } = useWizardQuestion({
    body: {
      applyItemActions: [],
      questionStrategy: wizardContextQuestionStrategyMappings[wizardContext],
      sourceDoc,
    },
  });

  const itemIdsWithQuestion = React.useMemo(() => {
    return data?.questions.map(q => q.itemId) ?? [];
  }, [data?.questions]);

  const hasItemAnyQuestions = React.useCallback(
    (itemId: string) => itemIdsWithQuestion.includes(itemId),
    [itemIdsWithQuestion]
  );

  const memoizedRefetch = React.useCallback(async () => {
    refetch();
  }, [refetch]);

  return [hasItemAnyQuestions, memoizedRefetch] as const;
}

const wizardContextQuestionStrategyMappings: Record<
  WizardContext,
  wizard.WizardConfigurationSchemaInput["questionStrategy"]
> = {
  item: { type: "item-each" },
  onQuoteRefinement: {
    type: "questionControl",
    askWhen: "onQuoteRefinement",
    askWhom: "contractor",
    startingNode: { type: "root" },
  },
  onQuoteRefinement2: {
    type: "questionControl",
    askWhen: "onQuoteRefinement2",
    askWhom: "contractor",
    startingNode: { type: "root" },
  },
  onQuoteRefinement3: {
    type: "questionControl",
    askWhen: "onQuoteRefinement3",
    askWhom: "contractor",
    startingNode: { type: "root" },
  },
};
